import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import appConfig from 'app.config'
import {
	checkIsMobile,
	getErrorWithFullMessage,
	getLanguageLocale,
	isSafariBrowser,
} from 'shared'
import Tooltip from 'shared/components/Tooltip'

import SspContext from '../../../../contexts/SspContext'
import {
	IAccountStatements,
	IDepositData,
	IMSAgreementStatus,
} from '../../../../interfaces/i-api-creaditCard'
import getAccountStatementService from '../../../../services/CreditCardServices/getAccountStatementService'
import getAccountStatementsService from '../../../../services/CreditCardServices/getAccountStatementsService'
import getDepositAccountStatementsService from '../../../../services/DepositServices/getDepositAccountStatementsService'
import getDepositAccountStatementService from '../../../../services/DepositServices/getDepositAccountStatementService'
import { TableLegacy } from '../../../../components/index'

import './PaymentInformation.scss'
import { CustomerStatus } from 'selfServicePortal/interfaces/i-api-customer'
import { useSspContext } from 'selfServicePortal/hooks/useSspContext'

const PaymentInformation = () => {
	const { t } = useTranslation()
	const isMobile = checkIsMobile()

	const { isCard, overviewData, productType } = useContext(SspContext)
	const { customerData } = useSspContext()
	const title =
		productType === 'Card'
			? 'MasterCardInformation'
			: productType === 'Deposit_Fixed'
				? 'DepositFixedInformation'
				: 'DepositInformation'

	const fileName = isCard?.isCard ? 'Invoice' : 'BankStatement'
	const archiveTitle = isCard?.isCard
		? 'InvoiceArchive'
		: 'AccountStatementArchive'
	const archiveDescription = isCard?.isCard
		? 'InvoiceArchiveDescription'
		: 'StatementArchiveDescription'

	const [accountStatements, setAccountStatements] =
		useState<IAccountStatements>()

	let bankAccountNumbers =
		appConfig.country === 'de' ||
		appConfig.country === 'at' ||
		appConfig.country === 'es' ||
		appConfig.country === 'it'
			? isCard?.isCard
				? 'SOBKDEB2XXX'
				: 'IBAN DE06202208000000024118 \n BIC SXPYDEHH'
			: isCard?.isCard
				? '3633.32.77160'
				: '3633.53.85432'

	const getAccountStatements = async () => {
		try {
			setLoading(true)
			if (isCard?.isCard) {
				let accountStatements = await getAccountStatementsService(
					t,
					overviewData?.accountOwnerPartyId!,
				)
				setAccountStatements(accountStatements)
				setLoading(false)
			} else {
				let accountStatements =
					await getDepositAccountStatementsService(
						t,
						overviewData?.accountOwnerPartyId!,
					)
				setAccountStatements(accountStatements)
				setLoading(false)
			}
		} catch (error: any) {
			setLoading(false)
			setError(getErrorWithFullMessage(error))
		}
	}

	useEffect(() => {
		getAccountStatements()
		// eslint-disable-next-line
	}, [])

	const depositData = [
		overviewData?.accountNumber,
		bankAccountNumbers,
		appConfig.country === 'de' ||
		appConfig.country === 'at' ||
		appConfig.country === 'es' ||
		appConfig.country === 'it'
			? 'Avarda Bank AB'
			: 'TF Bank - powered by Avarda (org.nr 923 194 592)',
	]

	let depositItems = depositData.map((item: any, key: number) => {
		return (
			<td key={key} style={{ whiteSpace: 'pre-line' }}>
				{item}
			</td>
		)
	})

	const DownloadItem = (props: any) => (
		<button
			onClick={(event: any) => {
				props.onClick()
			}}
			className={
				isMobile
					? 'd-flex justify-content-between btn-nostyle w-100'
					: 'btn-nostyle'
			}>
			{props.item} <span className="download-icon ml-2"></span>
		</button>
	)

	interface ITableAccountStatement {
		period: string
		minAmountToPay: string
		downloadPdf: any
	}

	let depositCols = [
		<div className="d-flex">
			{(appConfig.country === 'de' && isCard?.isCard) ||
			(appConfig.country === 'es' && isCard?.isCard) ||
			(appConfig.country === 'it' && isCard?.isCard) ||
			(appConfig.country === 'at' && isCard?.isCard)
				? t('Viban')
				: t('CustomerReferenceNumber')}

			<Tooltip
				item={{
					value:
						(appConfig.country === 'de' && !isCard?.isCard) ||
						(appConfig.country === 'es' && isCard?.isCard) ||
						(appConfig.country === 'it' && isCard?.isCard) ||
						(appConfig.country === 'at' && !isCard?.isCard)
							? t('CustRefDepositTooltip')
							: t('CustRefTooltip'),
				}}
			/>
		</div>,
		<>
			{(appConfig.country === 'de' && isCard?.isCard) ||
			(appConfig.country === 'es' && isCard?.isCard) ||
			(appConfig.country === 'it' && isCard?.isCard) ||
			(appConfig.country === 'at' && isCard?.isCard)
				? t('Bic')
				: t('BankAccountNumber')}
		</>,
		<>{t('Reciever')}</>,
	]

	const [tableData, setTableData] = useState<ITableAccountStatement[]>([])
	const [error, setError] = useState<Error | undefined>()
	const [loading, setLoading] = useState(false)

	const getAccountStatement = useCallback(
		async (
			accountStatementId: string,
			fileName: string,
			windowRef: Window | null,
		) => {
			try {
				if (isCard?.isCard) {
					await getAccountStatementService(
						accountStatementId,
						t,
						windowRef,
					)
				} else {
					await getDepositAccountStatementService(
						accountStatementId,
						fileName,
						t,
						windowRef,
					)
				}
			} catch (error: any) {
				setError(getErrorWithFullMessage(error))
			}
		},
		// eslint-disable-next-line
		[],
	)

	useEffect(() => {
		if (accountStatements?.accountStatements) {
			accountStatements.accountStatements =
				accountStatements.accountStatements.sort((a, b) => {
					return (
						+new Date(b.accountStatementDate) -
						+new Date(a.accountStatementDate)
					)
				})

			setTableData(
				accountStatements.accountStatements.map((a) => {
					return {
						period: `${new Date(
							a.periodStartDate,
						).toLocaleDateString(getLanguageLocale())}-${new Date(
							a.periodEndDate,
						).toLocaleDateString(getLanguageLocale())}`,
						minAmountToPay: `${a.paymentAmount} ${a.paymentCurrency}`,
						downloadPdf: (
							<DownloadItem
								item={`${t(fileName)} ${new Date(
									a.accountStatementDate,
								).toLocaleString(getLanguageLocale(), {
									month: 'long',
									year: 'numeric',
								})}`}
								onClick={() => {
									let windowRef = isSafariBrowser()
										? window.open()
										: null
									getAccountStatement(
										a.accountStatementId,
										`${t(fileName)}_${new Date(
											a.accountStatementDate,
										).toLocaleDateString(
											getLanguageLocale(),
										)}.pdf`,
										windowRef,
									)
								}}
							/>
						),
					}
				}),
			)
		}
		// eslint-disable-next-line
	}, [accountStatements])

	let invoiceArchiveItems
	if (tableData) {
		invoiceArchiveItems = tableData.map((item: any, key: number) => {
			return (
				<tr key={key}>
					<td>{item.period}</td>
					<td>{isCard?.isCard && item.minAmountToPay}</td>
					<td>{item.downloadPdf}</td>
				</tr>
			)
		})
	}

	const PaymenyInfoMobile = () => (
		<div>
			{customerData?.status !== CustomerStatus.Closed &&
				(overviewData as IDepositData)?.agreementStatus !==
					IMSAgreementStatus.Closed && (
					<div className="outer-container mt-4 p-3">
						<div className="d-flex flex-column">
							<h3>{t(title)}</h3>
						</div>
						<div>
							<div className="my-3">
								<b>{t('CustomerReferenceNumber')}</b>
								<div className="d-flex">
									{depositData[0]}
									<Tooltip
										item={{ value: t('CustRefTooltip') }}
									/>
								</div>
							</div>
							<div className="my-3">
								<b>{t('BankAccountNumber')}</b>
								<div style={{ whiteSpace: 'pre-line' }}>
									{depositData[1]}
								</div>
							</div>
							<div className="my-3">
								<b>{t('Reciever')}</b>
								<div>{depositData[2]}</div>
							</div>
						</div>
					</div>
				)}

			<div className="mt-5">
				<div className="d-flex flex-column">
					<h3>{t(archiveTitle)}</h3>
					{appConfig.country === 'de' && (
						<div>{t(archiveDescription)}</div>
					)}
				</div>
				{error ? (
					<div className="error-msg mt-3">{t(error.message)}</div>
				) : (
					<div>
						<div className="d-flex justify-content-between my-3">
							<div>{t('Period')}</div>
							<div>{t('DownloadPDF')}</div>
						</div>
						{tableData.map((i, key) => {
							return (
								<div className="mobile-item py-3" key={key}>
									{i.downloadPdf}
								</div>
							)
						})}
					</div>
				)}
			</div>
		</div>
	)
	return (
		<>
			{loading ? (
				<div id="circle-spinner"></div>
			) : (
				<div className="fade-in container payment-info">
					{isMobile ? (
						<PaymenyInfoMobile />
					) : (
						<>
							{customerData?.status !== CustomerStatus.Closed &&
								(overviewData as IDepositData)
									?.agreementStatus !==
									IMSAgreementStatus.Closed && (
									<div className="outer-container mt-4 p-3">
										<div className="d-flex flex-column">
											<h3>{t(title)}</h3>
										</div>
										<TableLegacy
											columns={depositCols}
											data={<tr>{depositItems}</tr>}
										/>
									</div>
								)}

							<div className="outer-container mt-5 p-3">
								<div className="d-flex flex-column">
									<h3>{t(archiveTitle)}</h3>
									{appConfig.country === 'de' && (
										<div>{t(archiveDescription)}</div>
									)}
								</div>
								{error ? (
									<div className="error-msg my-3">
										{t(error.message)}
									</div>
								) : (
									<TableLegacy
										columns={[
											t('Period'),
											isCard?.isCard &&
												t('MinAmountToPay'),
											t('DownloadPDF'),
										]}
										data={invoiceArchiveItems}
									/>
								)}
							</div>
						</>
					)}
				</div>
			)}
		</>
	)
}
export default PaymentInformation
